import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Upload, Camera, Loader2, Sparkles } from "lucide-react";
import { API } from "@/App";

const CATEGORIES = [
  "Electronics",
  "Clothing",
  "Food & Beverage",
  "Home & Garden",
  "Office Supplies",
  "Health & Beauty",
  "Sports & Outdoors",
  "Automotive",
  "Toys & Games",
  "Other",
];

export default function ProductForm({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({
    sku: "",
    name: "",
    barcode: "",
    category: "",
    brand: "",
    description: "",
    cost_price: "",
    retail_price: "",
    wholesale_price: "",
    image_url: "",
    supplier_id: "",
    low_stock_threshold: "10",
  });

  useEffect(() => {
    fetchSuppliers();
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API}/suppliers`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/products/${id}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setForm({
          sku: data.sku || "",
          name: data.name || "",
          barcode: data.barcode || "",
          category: data.category || "",
          brand: data.brand || "",
          description: data.description || "",
          cost_price: data.cost_price?.toString() || "",
          retail_price: data.retail_price?.toString() || "",
          wholesale_price: data.wholesale_price?.toString() || "",
          image_url: data.image_url || "",
          supplier_id: data.supplier_id || "",
          low_stock_threshold: data.low_stock_threshold?.toString() || "10",
        });
      } else {
        toast.error("Product not found");
        navigate("/products");
      }
    } catch (error) {
      toast.error("Error fetching product");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageAnalysis = async (file) => {
    if (!file) return;
    
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/ai/analyze-product`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setForm((prev) => ({
          ...prev,
          name: data.name || prev.name,
          category: data.category || prev.category,
          brand: data.brand || prev.brand,
          description: data.description || prev.description,
        }));
        toast.success("AI analysis complete!");
      } else {
        toast.error("Failed to analyze image");
      }
    } catch (error) {
      toast.error("Error analyzing image");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageAnalysis(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.sku || !form.name) {
      toast.error("SKU and Name are required");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        cost_price: parseFloat(form.cost_price) || 0,
        retail_price: parseFloat(form.retail_price) || 0,
        wholesale_price: parseFloat(form.wholesale_price) || 0,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 10,
        supplier_id: form.supplier_id || null,
      };

      const url = isEdit ? `${API}/products/${id}` : `${API}/products`;
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (response.ok) {
        toast.success(isEdit ? "Product updated" : "Product created");
        navigate("/products");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save product");
      }
    } catch (error) {
      toast.error("Error saving product");
    } finally {
      setLoading(false);
    }
  };

  const margin = form.retail_price && form.cost_price
    ? ((parseFloat(form.retail_price) - parseFloat(form.cost_price)) / parseFloat(form.retail_price) * 100).toFixed(1)
    : 0;

  const profit = form.retail_price && form.cost_price
    ? (parseFloat(form.retail_price) - parseFloat(form.cost_price)).toFixed(2)
    : 0;

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="product-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/products")}
            className="rounded-sm"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              {isEdit ? "Edit Product" : "Add Product"}
            </h1>
            <p className="text-muted-foreground">
              {isEdit ? "Update product details" : "Create a new product"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* AI Analysis Card */}
          <Card className="rounded-sm border border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">AI Product Recognition</p>
                    <p className="text-sm text-muted-foreground">
                      Upload a photo to auto-fill product details
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={analyzing}
                    className="rounded-sm"
                    data-testid="ai-analyze-btn"
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4 mr-2" />
                        Upload Photo
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-base font-heading">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU *</Label>
                  <Input
                    id="sku"
                    value={form.sku}
                    onChange={(e) => handleChange("sku", e.target.value)}
                    placeholder="e.g., PROD-001"
                    className="rounded-sm font-data"
                    required
                    data-testid="sku-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="barcode">Barcode</Label>
                  <Input
                    id="barcode"
                    value={form.barcode}
                    onChange={(e) => handleChange("barcode", e.target.value)}
                    placeholder="UPC/EAN barcode"
                    className="rounded-sm font-data"
                    data-testid="barcode-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Product name"
                  className="rounded-sm"
                  required
                  data-testid="name-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={form.category} onValueChange={(v) => handleChange("category", v)}>
                    <SelectTrigger className="rounded-sm" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    value={form.brand}
                    onChange={(e) => handleChange("brand", e.target.value)}
                    placeholder="Brand name"
                    className="rounded-sm"
                    data-testid="brand-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Product description"
                  className="rounded-sm min-h-[80px]"
                  data-testid="description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-base font-heading">Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cost_price">Cost Price ($)</Label>
                  <Input
                    id="cost_price"
                    type="number"
                    step="0.01"
                    value={form.cost_price}
                    onChange={(e) => handleChange("cost_price", e.target.value)}
                    placeholder="0.00"
                    className="rounded-sm font-data"
                    data-testid="cost-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retail_price">Retail Price ($)</Label>
                  <Input
                    id="retail_price"
                    type="number"
                    step="0.01"
                    value={form.retail_price}
                    onChange={(e) => handleChange("retail_price", e.target.value)}
                    placeholder="0.00"
                    className="rounded-sm font-data"
                    data-testid="retail-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wholesale_price">Wholesale Price ($)</Label>
                  <Input
                    id="wholesale_price"
                    type="number"
                    step="0.01"
                    value={form.wholesale_price}
                    onChange={(e) => handleChange("wholesale_price", e.target.value)}
                    placeholder="0.00"
                    className="rounded-sm font-data"
                    data-testid="wholesale-price-input"
                  />
                </div>
              </div>

              {/* Margin Display */}
              {form.cost_price && form.retail_price && (
                <div className="p-4 rounded-sm bg-muted">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Profit/Unit</p>
                      <p className={`font-data text-xl font-semibold ${parseFloat(profit) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                        ${profit}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Margin</p>
                      <p className={`font-data text-xl font-semibold ${parseFloat(margin) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                        {margin}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-base font-heading">Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="supplier_id">Supplier</Label>
                  <Select value={form.supplier_id || "none"} onValueChange={(v) => handleChange("supplier_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No supplier</SelectItem>
                      {suppliers.map((sup) => (
                        <SelectItem key={sup.supplier_id} value={sup.supplier_id}>
                          {sup.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="low_stock_threshold">Low Stock Threshold</Label>
                  <Input
                    id="low_stock_threshold"
                    type="number"
                    value={form.low_stock_threshold}
                    onChange={(e) => handleChange("low_stock_threshold", e.target.value)}
                    placeholder="10"
                    className="rounded-sm font-data"
                    data-testid="threshold-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  value={form.image_url}
                  onChange={(e) => handleChange("image_url", e.target.value)}
                  placeholder="https://..."
                  className="rounded-sm"
                  data-testid="image-url-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/products")}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm flex-1 sm:flex-none"
              data-testid="submit-product-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                isEdit ? "Update Product" : "Create Product"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
